import Vue from 'vue'
import App from './App.vue'

import router from '@/routers'
import './registerServiceWorker'
import ArgonDashboard from './plugins/schooldock-dashboard'
import moment from 'moment'

import axios from "axios";
import vuex from "vuex";
import store from './store';
import { queueOperation, registerSyncHandler, startSyncService } from '@/rxdb/offlineSync';
import { getCurrentUserScope } from '@/rxdb/sessionScope';


Vue.prototype.$http = axios;
axios.defaults.baseURL = `${process.env.VUE_APP_ENV_API_URL_LIVE}`;

registerSyncHandler('http', async operation => {
  const token = store.getters['auth/_userAuthToken'] || store.state.auth.token;
  const response = await axios({
    method: operation.method,
    url: operation.url,
    data: operation.payload,
    __offlineReplay: true,
    headers: {
      ...operation.headers,
      ...(token ? { Authorization: `bearer ${token}` } : {})
    }
  });
  return response.data;
});

async function queueOfflineConfig(config) {
  let payload = config.data || {};
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = { value: payload };
    }
  }
  if (typeof payload !== 'object' || payload === null) payload = { value: payload };

  const operationId = config.__offlineOperationId ||
    `http:${String(config.method).toLowerCase()}:${config.url}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  const requestHeaders = {
    ...(config.headers || {}),
    'X-Idempotency-Key': operationId
  };

  await queueOperation({
    id: operationId,
    operationType: 'http',
    entityKey: `${String(config.method).toUpperCase()}:${config.url}`,
    method: String(config.method).toUpperCase(),
    url: config.url,
    payload,
    headers: requestHeaders
  });

  return {
    config,
    responseCode: '00',
    data: payload,
    offline: true
  };
}

axios.interceptors.request.use(async config => {
  const method = String(config.method || 'get').toLowerCase();
  const mutating = ['post', 'put', 'patch', 'delete'].includes(method);
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const isMultipart = typeof FormData !== 'undefined' && config.data instanceof FormData;
  const requestUrl = String(config.url || '').toLowerCase();
  const isSensitive = /auth|login|payment|verify|upload/.test(requestUrl);

  if (mutating && !config.__offlineReplay && !isMultipart && !isSensitive) {
    config.__offlineOperationId = config.__offlineOperationId ||
      (config.data && config.data.ClientRequestId) ||
      `http:${method}:${config.url}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    config.headers = {
      ...(config.headers || {}),
      'X-Idempotency-Key': config.__offlineOperationId
    };
  }

  if (!mutating || !offline || config.__offlineReplay || isMultipart || isSensitive) return config;

  const queuedResponse = await queueOfflineConfig(config);
  return Promise.reject({
    isOfflineQueued: true,
    config,
    offlineResponse: queuedResponse
  });
});

startSyncService();


Vue.config.productionTip = false
Vue.filter('formatDate', function (value) {
  if (value) {  

    return moment(String(value)).format(value || 'MMM Do, YYYY');
  }
});
Vue.prototype.$frontEndDateFormat = function(date) {
  if (!date) return '..../../..';
  
  const s = String(date).trim();
  
  // Handle common default DB date (SQL Server .NET default) and empty indicators
  if (s === '' || s.startsWith('0001-01-01')) return '';
  
  const m = moment(date);
  if (!m.isValid()) return '..../../..';
  
  return m.format("YYYY-MM-DD");
};
let authRedirectInProgress = false;
let tabAuthScope = getCurrentUserScope();

axios.interceptors.response.use(response => {
    return response;
}, error => {
    if (error && error.isOfflineQueued) {
      return { config: error.config, status: 202, data: error.offlineResponse };
    }
    const config = error && error.config;
    const method = config && String(config.method || '').toLowerCase();
    const requestUrl = config && String(config.url || '').toLowerCase();
    const replayable = config && ['post', 'put', 'patch', 'delete'].includes(method) &&
      !config.__offlineReplay && !/auth|login|payment|verify|upload/.test(requestUrl) &&
      !(typeof FormData !== 'undefined' && config.data instanceof FormData);
    const transientFailure = !error.response || error.response.status >= 500;
    if (replayable && transientFailure) {
      return queueOfflineConfig(config)
        .then(offlineResponse => ({ config, status: 202, data: offlineResponse }));
    }
    const status = error && error.response && error.response.status;
    const isLoginRequest = /portalauth\/auth|portalauth\/validatetoken|retrievepassword/.test(requestUrl);

    if (status === 401 && !isLoginRequest && !authRedirectInProgress) {
      authRedirectInProgress = true;
      const currentRoute = router.currentRoute;
      const redirect = currentRoute && currentRoute.fullPath !== "/login"
        ? currentRoute.fullPath
        : undefined;

      store.dispatch("auth/Logout").finally(() => {
        if (router.currentRoute.name !== "Login") {
          router.replace({
            name: "Login",
            query: redirect ? { redirect } : undefined,
          }).catch(() => {});
        }
      });
    }
    return Promise.reject(error);
});

router.afterEach((to) => {
  if (to.name !== "Login") authRedirectInProgress = false;
});

window.addEventListener("storage", (event) => {
  if (event.key === "schooldock-auth-scope") {
    if (event.newValue && tabAuthScope && event.newValue !== tabAuthScope) {
      const currentRoute = router.currentRoute;
      const requiresAuthentication = (currentRoute.matched || []).some(
        (record) => record.meta && record.meta.user
      );
      store.commit('auth/clear_external_session');
      if (requiresAuthentication && currentRoute.name !== "Login") {
        router.replace({
          name: "Login",
          query: currentRoute.fullPath ? { redirect: currentRoute.fullPath } : undefined,
        }).catch(() => {});
      }
    }
    tabAuthScope = event.newValue;
    return;
  }

  if (event.key !== "#") return;
  if (tabAuthScope && getCurrentUserScope() !== tabAuthScope) return;

  if (!event.newValue) {
    const currentRoute = router.currentRoute;
    const requiresAuthentication = (currentRoute.matched || []).some(
      (record) => record.meta && record.meta.user
    );
    store.dispatch("auth/Logout").then(() => {
      if (requiresAuthentication && router.currentRoute.name !== "Login") {
        router.replace({
          name: "Login",
          query: currentRoute.fullPath ? { redirect: currentRoute.fullPath } : undefined,
        }).catch(() => {});
      }
    });
  } else if (event.newValue !== store.state.auth.token) {
    store.commit("auth/auth_token", { token: event.newValue });
  }
});


Vue.config.productionTip = false

Vue.use(vuex)

Vue.use(ArgonDashboard)
new Vue({
   
    store,
    router,    
    render: (h) => h(App),
}).$mount("#app");
